export type Bird = {
  id: string;
  name: string;
  variant?: string;
  images: string[];
  group: BirdGroup;
  habitat: Habitat;
  difficulty: 1 | 2 | 3;
  intro: string;
  memory: string;
  keyMarks: [string, string, string];
  similar: {
    name: string;
    difference: string;
  };
};

export type BirdGroup =
  | "雁鸭与游禽"
  | "鹭类与秧鸡"
  | "滨海涉禽与鸥"
  | "林鸟与灌丛鸟"
  | "城市与田野鸟"
  | "天空飞鸟与猛禽";

export type Habitat =
  | "湖泊河流"
  | "海岸滩涂"
  | "芦苇湿地"
  | "林地灌丛"
  | "城市田野"
  | "山地溪流"
  | "开阔天空";

export const groupMeta: Record<
  BirdGroup,
  { short: string; description: string; tone: string }
> = {
  雁鸭与游禽: {
    short: "水面",
    description: "先看体形，再抓嘴、头和翼镜",
    tone: "sage"
  },
  鹭类与秧鸡: {
    short: "水边",
    description: "长腿涉水或贴着芦苇潜行",
    tone: "blue"
  },
  滨海涉禽与鸥: {
    short: "滩涂",
    description: "嘴形、腿色和飞行翼纹最关键",
    tone: "sand"
  },
  林鸟与灌丛鸟: {
    short: "林灌",
    description: "用头纹、翼斑和尾色拆解小鸟",
    tone: "moss"
  },
  城市与田野鸟: {
    short: "身边",
    description: "从每天都能遇见的鸟开始",
    tone: "clay"
  },
  天空飞鸟与猛禽: {
    short: "天空",
    description: "轮廓比颜色更可靠",
    tone: "sky"
  }
};

export const birds: Bird[] = [
  {
    id: "001",
    name: "翘鼻麻鸭",
    images: ["/birds/001.webp"],
    group: "雁鸭与游禽",
    habitat: "海岸滩涂",
    difficulty: 1,
    intro:
      "体形较大的鸭类，常在海湾、河口和咸淡水湖的浅水区成群取食。黑白栗三色分区鲜明，远距离也很醒目。",
    memory: "黑头白身系栗带，红嘴微翘。",
    keyMarks: ["黑绿色头颈", "胸前宽栗色环带", "红色上翘嘴"],
    similar: {
      name: "赤麻鸭",
      difference:
        "赤麻鸭全身以橙褐色为主、头较浅；翘鼻麻鸭是黑头、白身并有宽栗色胸带。"
    }
  },
  {
    id: "002",
    name: "鸳鸯",
    variant: "雄鸟",
    images: ["/birds/002.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "繁殖羽雄鸟是中国最容易辨认的鸭类之一，喜欢有林木遮蔽的湖泊、溪流和水库，也会停栖在水边树枝上。",
    memory: "橙色帆羽立在背，白眉一路弯到颈。",
    keyMarks: ["夸张的橙色帆羽", "宽白眉和栗色脸侧", "红嘴与多彩体羽"],
    similar: {
      name: "鸳鸯雌鸟",
      difference:
        "雄鸟色彩强烈并有帆状三级飞羽；雌鸟灰褐色，最醒目的特征是白色眼圈及向后延伸的眼纹。"
    }
  },
  {
    id: "003",
    name: "鸳鸯",
    variant: "雌鸟",
    images: ["/birds/003.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 2,
    intro:
      "雌鸟远比雄鸟朴素，常在林缘水面活动。观察眼周白纹、灰色头部和细密斑点，比只看整体褐色更可靠。",
    memory: "灰褐小鸭戴白眼镜，眼线拖向脑后。",
    keyMarks: ["完整白眼圈", "白眼纹向后延伸", "灰头与点斑体侧"],
    similar: {
      name: "绿头鸭雌鸟",
      difference:
        "绿头鸭雌鸟全身粗密杂斑，嘴呈橙黑相间；鸳鸯雌鸟有清楚的白眼圈和后伸白眼纹。"
    }
  },
  {
    id: "004",
    name: "绿头鸭",
    variant: "雌鸟",
    images: ["/birds/004.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 2,
    intro:
      "广泛出现在城市湖泊、河流和湿地。雌鸟全身褐色杂斑，但橙黑相间的嘴和蓝色翼镜能迅速缩小判断范围。",
    memory: "褐色迷彩身，橙黑花嘴加蓝翼镜。",
    keyMarks: ["全身粗密褐斑", "橙色嘴上有黑斑", "蓝紫色翼镜"],
    similar: {
      name: "斑嘴鸭",
      difference:
        "斑嘴鸭的嘴以黑色为主、末端鲜黄，脸上常有淡眉纹；绿头鸭雌鸟的嘴明显橙黑相间。"
    }
  },
  {
    id: "005",
    name: "绿头鸭",
    variant: "雄鸟",
    images: ["/birds/005.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "最熟悉的野鸭之一，能够适应天然湿地和城市水面。繁殖羽雄鸟的头部会呈现金属绿色光泽。",
    memory: "绿头、白领、栗胸、黄嘴。",
    keyMarks: ["金属绿色头部", "细白颈环", "栗色胸与黄色嘴"],
    similar: {
      name: "斑嘴鸭",
      difference:
        "雄性斑嘴鸭没有绿色头部和白颈环，嘴黑而端部黄；雄性绿头鸭的四色分区十分清楚。"
    }
  },
  {
    id: "006",
    name: "戴胜",
    images: ["/birds/006.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常在草地、果园和疏林地面探取昆虫。受惊或兴奋时会展开扇形羽冠，飞行时黑白双翼格外醒目。",
    memory: "长弯嘴、扇羽冠、黑白斑马翼。",
    keyMarks: ["黑端扇形羽冠", "细长下弯嘴", "黑白横斑双翼"],
    similar: {
      name: "大斑啄木鸟",
      difference:
        "大斑啄木鸟为黑白红配色、嘴粗直如凿；戴胜体色粉褐，嘴细长下弯并有可展开的羽冠。"
    }
  },
  {
    id: "007",
    name: "普通翠鸟",
    images: ["/birds/007.webp"],
    group: "鹭类与秧鸡",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "常静立在水边低枝或石块上，随后直冲水面捕鱼。体形虽小，蓝绿色背部与橙色腹部的反差非常醒目。",
    memory: "大头长嘴短尾，背蓝腹橙守水边。",
    keyMarks: ["亮蓝绿色上体", "橙栗色下体", "长直嘴与极短尾"],
    similar: {
      name: "白胸翡翠",
      difference:
        "白胸翡翠体形更大，头和腹部栗褐、胸前大片白色且嘴鲜红；普通翠鸟更小，背部更亮蓝。"
    }
  },
  {
    id: "008",
    name: "池鹭",
    images: ["/birds/008.webp"],
    group: "鹭类与秧鸡",
    habitat: "芦苇湿地",
    difficulty: 2,
    intro:
      "常在稻田、池塘和沼泽边缓慢行走。繁殖期头颈栗红、背部暗紫，起飞后原本隐藏的白色双翼突然显现。",
    memory: "站着栗红，飞起一对白翅。",
    keyMarks: ["栗红色头颈", "暗紫褐色背", "飞行时双翼纯白"],
    similar: {
      name: "夜鹭",
      difference:
        "夜鹭更粗壮、颈更短，成鸟黑背灰翼；池鹭体形纤细，繁殖羽栗红，飞行时白翼最显眼。"
    }
  },
  {
    id: "009",
    name: "苍鹭",
    images: ["/birds/009.webp"],
    group: "鹭类与秧鸡",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "中国常见的大型鹭类，常独自在浅水中等待鱼类靠近。飞行时颈部收成明显的 S 形，长腿伸在尾后。",
    memory: "灰色大鹭，黑眉长枪嘴。",
    keyMarks: ["大型灰色体羽", "黑色贯眼纹和饰羽", "粗长黄色匕首状嘴"],
    similar: {
      name: "大白鹭",
      difference:
        "大白鹭通体白色；苍鹭以灰色为主，头侧有宽黑纹，飞行时黑灰翼色也很明显。"
    }
  },
  {
    id: "010",
    name: "山斑鸠",
    images: ["/birds/010.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常见于农田、村落和疏林，通常在地面取食种子。翼上覆羽呈鳞片状，颈侧带有黑白相间的横纹。",
    memory: "鳞片花翅，颈侧黑白条纹围巾。",
    keyMarks: ["黑白横纹颈斑", "栗褐色鳞状翼斑", "粉褐色胸部"],
    similar: {
      name: "珠颈斑鸠",
      difference:
        "珠颈斑鸠的颈斑像黑底白点的珍珠项圈；山斑鸠的颈斑是规整的黑白横条。"
    }
  },
  {
    id: "011",
    name: "中杓鹬",
    images: ["/birds/011.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 2,
    intro:
      "迁徙季常见于海滩、河口和泥滩，以向下弯曲的嘴探取底栖动物。头顶的三道明暗纵纹是远距离判断重点。",
    memory: "中等弯嘴，头顶一浅两深三道纹。",
    keyMarks: ["明显浅色中央冠纹", "两侧深色冠纹", "中等长度下弯嘴"],
    similar: {
      name: "大杓鹬",
      difference:
        "大杓鹬体形明显更大、嘴极长且头纹较平淡；中杓鹬较小，头顶三道纵纹非常清楚。"
    }
  },
  {
    id: "012",
    name: "大杓鹬",
    images: ["/birds/012.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 2,
    intro:
      "世界上体形最大的鹬类之一，主要在沿海滩涂用超长嘴深入泥中觅食。中国东部沿海是其迁徙和越冬的重要区域。",
    memory: "体形最大，嘴长得夸张，腰和翼下偏褐。",
    keyMarks: ["极长而均匀下弯的嘴", "大型深褐色体形", "褐色腰与翼下"],
    similar: {
      name: "白腰杓鹬",
      difference:
        "白腰杓鹬飞行时有醒目的白色腰和伸向背部的白楔；大杓鹬腰、背和翼下保持褐色。"
    }
  },
  {
    id: "013",
    name: "翻石鹬",
    images: ["/birds/013.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 1,
    intro:
      "偏爱岩礁、砾石滩和海堤，会用短而结实的嘴翻动石块寻找食物。体形紧凑，腿呈橙红色。",
    memory: "橙腿短嘴，在石头间翻找的花斑鹬。",
    keyMarks: ["橙红色短腿", "短而楔形的嘴", "黑白栗色块状斑纹"],
    similar: {
      name: "矶鹬",
      difference:
        "矶鹬体形更修长、上体素褐、腿色偏绿且常点尾；翻石鹬更敦实，橙腿和块状花纹鲜明。"
    }
  },
  {
    id: "014",
    name: "蛎鹬",
    images: ["/birds/014.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 1,
    intro:
      "多见于潮间带和岩礁海岸，粗长嘴适合撬开或切断贝类。黑白身体配上红嘴红腿，辨识度极高。",
    memory: "黑白礼服，红嘴红腿撬贝壳。",
    keyMarks: ["长而粗直的橙红嘴", "黑白分明的体羽", "粉红至红色腿"],
    similar: {
      name: "黑翅长脚鹬",
      difference:
        "黑翅长脚鹬的粉腿极长、嘴细黑；蛎鹬腿较短，嘴粗厚鲜红，身体也更加敦实。"
    }
  },
  {
    id: "015",
    name: "红嘴鸥",
    images: ["/birds/015.webp"],
    group: "滨海涉禽与鸥",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "冬季大量出现在城市湖泊、江河和沿海。非繁殖羽头部白而带深色耳斑，繁殖期则换上深褐色头罩。",
    memory: "小型浅灰鸥，红嘴红脚，冬天留一枚黑耳斑。",
    keyMarks: ["红色嘴和腿", "浅灰背与黑色翼尖", "冬羽深色耳斑"],
    similar: {
      name: "黑尾鸥",
      difference:
        "黑尾鸥体形更大，黄嘴带红黑端斑，成鸟有黑色尾带；红嘴鸥较小，嘴腿均红且成鸟尾白。"
    }
  },
  {
    id: "016",
    name: "夜鹭",
    images: ["/birds/016.webp"],
    group: "鹭类与秧鸡",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "粗壮短颈的鹭类，黄昏后最活跃，白天常缩颈停在水边树上。成鸟红眼，头顶和背部黑色。",
    memory: "缩着脖子的黑背灰翼红眼鹭。",
    keyMarks: ["黑色头顶与背", "灰色双翼和白色腹部", "红眼与短粗黄嘴"],
    similar: {
      name: "池鹭",
      difference:
        "池鹭更纤细、繁殖羽栗红且飞行时白翼醒目；夜鹭短颈粗壮，成鸟为黑、灰、白三色。"
    }
  },
  {
    id: "017",
    name: "大斑啄木鸟",
    images: ["/birds/017.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "常见于各种树林和城市公园，沿树干攀爬并敲击树皮取食。黑白背部、白肩斑和鲜红尾下覆羽是核心组合。",
    memory: "黑白花背大白肩，尾巴下面一点红。",
    keyMarks: ["大片白色肩斑", "黑白相间的背翼", "鲜红色尾下覆羽"],
    similar: {
      name: "灰头绿啄木鸟",
      difference:
        "灰头绿啄木鸟背部整体绿色、头灰；大斑啄木鸟背翼黑白分明，并有醒目的白肩斑。"
    }
  },
  {
    id: "018",
    name: "灰喜鹊",
    images: ["/birds/018.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常成家族群活动于公园、校园和疏林。体羽柔和灰褐，头顶黑色，长尾和双翼带蓝色光泽。",
    memory: "黑帽、灰身、蓝翅蓝长尾。",
    keyMarks: ["黑色头顶", "灰褐色身体", "蓝色双翼与长尾"],
    similar: {
      name: "喜鹊",
      difference:
        "喜鹊体形更大，黑白对比强烈；灰喜鹊身体灰褐，翼和尾呈柔和蓝色，常成群穿梭树间。"
    }
  },
  {
    id: "019",
    name: "乌鸫",
    images: ["/birds/019.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常见于城市绿地、庭院和林缘，喜欢在草地上跳跃寻找蚯蚓。雄鸟通体黑色，橙黄色嘴和眼圈非常突出。",
    memory: "一身黑，橙嘴配橙眼圈，长尾在草地跳。",
    keyMarks: ["雄鸟通体黑色", "橙黄色嘴", "细橙色眼圈"],
    similar: {
      name: "灰椋鸟",
      difference:
        "灰椋鸟体形更紧凑，灰褐身体上有白脸和白腰；雄性乌鸫更修长、通体黑并有较长尾。"
    }
  },
  {
    id: "020",
    name: "沼泽山雀",
    images: ["/birds/020.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "活跃在落叶林、河谷林和林缘，常倒挂枝头寻找昆虫。整体颜色朴素，黑帽、黑喉和浅色脸形成简洁对比。",
    memory: "黑帽黑小围兜，脸白肚子素。",
    keyMarks: ["亮黑色头顶", "小块黑色喉斑", "灰褐背与浅色腹"],
    similar: {
      name: "远东山雀",
      difference:
        "远东山雀腹部黄绿并有黑色纵带，后颈有白斑；沼泽山雀腹部素淡，没有贯穿腹部的黑带。"
    }
  },
  {
    id: "021",
    name: "远东山雀",
    images: ["/birds/021.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "树林和城市公园里的常见小鸟，叫声多变，常在枝叶间快速取食。黑头、白脸和腹部黑纵带很有代表性。",
    memory: "黑头白脸，黄绿肚皮中间一条黑领带。",
    keyMarks: ["黑头配大白颊", "黄绿色下体", "腹部中央黑纵带"],
    similar: {
      name: "沼泽山雀",
      difference:
        "沼泽山雀腹部灰白且只有小黑喉斑；远东山雀腹部偏黄绿，黑色纵带从喉部向下延伸。"
    }
  },
  {
    id: "022",
    name: "领雀嘴鹎",
    images: ["/birds/022.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "常见于中国南方和西南的林缘、公园及灌丛，粗厚的黄色嘴适合取食果实。颈后的白色宽领非常醒目。",
    memory: "黑头白项圈，嘴像一枚黄色小锥子。",
    keyMarks: ["粗厚黄色嘴", "黑色头部", "宽阔白色后颈环"],
    similar: {
      name: "白头鹎",
      difference:
        "白头鹎嘴细黑，头侧和后枕有大片白色；领雀嘴鹎嘴粗黄，白色集中成清楚的后颈环。"
    }
  },
  {
    id: "023",
    name: "黄臀鹎",
    images: ["/birds/023.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "常见于中国西南和南方的林缘、农地与城市绿化带。头部较黑，尾下覆羽是一块鲜亮的黄色。",
    memory: "黑头白喉，尾巴下面藏一团黄。",
    keyMarks: ["黑色头顶与脸侧", "白色喉部", "鲜黄色尾下覆羽"],
    similar: {
      name: "白头鹎",
      difference:
        "白头鹎后枕和脸侧有大片白色，尾下也偏白；黄臀鹎头更黑，尾下覆羽鲜黄。"
    }
  },
  {
    id: "024",
    name: "白头鹎",
    images: ["/birds/024.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "中国南方城市和乡村最常见的鸟之一，常停在树梢鸣叫，也会成群取食浆果。黑色头顶后方接大片白色。",
    memory: "黑帽白后脑，脸边一撇黑胡子。",
    keyMarks: ["白色后枕与脸侧", "黑色头顶", "黑色髭纹"],
    similar: {
      name: "黄臀鹎",
      difference:
        "黄臀鹎头部更黑、白色主要在喉部，尾下鲜黄；白头鹎有明显白后枕，尾下不呈亮黄。"
    }
  },
  {
    id: "025",
    name: "家燕",
    images: ["/birds/025.webp"],
    group: "天空飞鸟与猛禽",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "在人类聚居地附近繁殖，常在屋檐筑泥巢并低空追逐飞虫。成鸟外侧尾羽延长成细长的尾叉。",
    memory: "蓝黑背、栗红喉、剪刀长尾。",
    keyMarks: ["金属蓝黑色上体", "栗红色额与喉", "深叉形长尾"],
    similar: {
      name: "金腰燕",
      difference:
        "金腰燕有浅栗色腰斑和较淡的喉部；家燕腰部蓝黑，额和喉呈浓栗红色。"
    }
  },
  {
    id: "026",
    name: "红尾水鸲",
    variant: "雌鸟",
    images: ["/birds/026.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "山地溪流",
    difficulty: 2,
    intro:
      "常在山地急流、石滩和水坝附近点尾停栖。雌鸟身体灰褐，展开尾部时会闪出醒目的白色腰和外侧尾羽。",
    memory: "溪边灰褐鸟，尾巴一开闪白扇。",
    keyMarks: ["灰褐色全身", "白色腰斑", "白色外侧尾羽"],
    similar: {
      name: "北红尾鸲雌鸟",
      difference:
        "北红尾鸲雌鸟的腰和尾呈橙红色；红尾水鸲雌鸟在溪流边活动，腰和外侧尾羽是白色。"
    }
  },
  {
    id: "027",
    name: "红头长尾山雀",
    images: ["/birds/027.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "体形很小却拖着长尾，常成群穿行于林冠和灌丛。栗红头顶、黑色脸纹与银灰色脸颊组合鲜明。",
    memory: "小身体拖长尾，栗红帽配黑面罩。",
    keyMarks: ["栗红色头顶", "黑色眼罩和喉斑", "细长尾羽"],
    similar: {
      name: "远东山雀",
      difference:
        "远东山雀体形更大、尾较短，黑头白脸并有腹部黑带；红头长尾山雀有栗红帽和明显长尾。"
    }
  },
  {
    id: "028",
    name: "戴菊",
    images: ["/birds/028.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "中国体形最小的鸟类之一，多在针叶树冠不停搜寻昆虫。头顶黄色至橙色冠纹被两条黑纹夹住。",
    memory: "圆滚小鸟，黑边夹着一条金色头冠。",
    keyMarks: ["黄色或橙色中央冠纹", "冠纹两侧黑边", "两道浅色翼斑"],
    similar: {
      name: "黄腰柳莺",
      difference:
        "黄腰柳莺有醒目的浅色中央冠纹和鲜黄腰斑；戴菊头冠为黄橙色并带黑边，体形也更圆小。"
    }
  },
  {
    id: "029",
    name: "山鹛",
    images: ["/birds/029.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "主要活动于中国北方低山的浓密灌丛，常先听见叫声才看到鸟。长尾、宽白眉和胸部纵纹是辨识核心。",
    memory: "灌丛里一条长尾，白眉下面画黑眼线。",
    keyMarks: ["宽而长的白色眉纹", "深色贯眼纹", "喉胸细纵纹与长尾"],
    similar: {
      name: "画眉",
      difference:
        "画眉体形更大、通体更浓褐，白眼圈向后延伸但胸部较均匀；山鹛更灰淡，白眉宽且喉胸有纵纹。"
    }
  },
  {
    id: "030",
    name: "暗绿绣眼鸟",
    images: ["/birds/030.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "常成群在树冠中取食昆虫、花蜜和果实，也会出现在城市公园。完整而粗白的眼圈几乎不会认错。",
    memory: "黄绿色小鸟，戴一副完整白眼镜。",
    keyMarks: ["完整粗白眼圈", "黄绿色上体", "黄色喉部与尾下"],
    similar: {
      name: "柳莺类",
      difference:
        "柳莺通常以眉纹和翼斑为主，没有完整厚白眼圈；暗绿绣眼鸟的白眼圈环绕整只眼睛。"
    }
  },
  {
    id: "031",
    name: "巨嘴柳莺",
    images: ["/birds/031.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 3,
    intro:
      "迁徙季常在林下和灌丛低处活动，动作有力。与多数柳莺相比体形较大、嘴更粗，眉纹长而明显。",
    memory: "一只大号柳莺：粗嘴、粗腿、长白眉。",
    keyMarks: ["较粗长的嘴", "长而宽的浅色眉纹", "橄榄褐上体与黄褐腹部"],
    similar: {
      name: "褐柳莺",
      difference:
        "褐柳莺更小、更灰褐，嘴和腿都较细；巨嘴柳莺体形厚实，嘴明显更粗，下体也更偏黄褐。"
    }
  },
  {
    id: "032",
    name: "斑鸫",
    images: ["/birds/032.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "冬季常见于林地、果园和城市绿地，落地翻动枯叶觅食。胸腹密布黑色鳞斑，双翼常带栗红色调。",
    memory: "白眉、栗翅，胸前铺满黑鳞片。",
    keyMarks: ["明显浅色眉纹", "胸腹密集黑色鳞斑", "栗红色翼部"],
    similar: {
      name: "红尾鸫",
      difference:
        "红尾鸫的腰、尾和体侧更偏橙红，胸部黑色较少；斑鸫整体更黑，胸腹鳞斑更浓密。"
    }
  },
  {
    id: "033",
    name: "北红尾鸲",
    variant: "雄鸟",
    images: ["/birds/033.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常在村落、公园、山地林缘和建筑附近停栖，习惯抖动鲜艳的尾部。雄鸟黑脸灰顶，翼上有大白斑。",
    memory: "灰帽黑脸白翼斑，肚子尾巴橙红色。",
    keyMarks: ["灰色头顶与黑色脸喉", "大片白色翼斑", "橙红色腹部和尾"],
    similar: {
      name: "红尾水鸲雄鸟",
      difference:
        "红尾水鸲雄鸟全身石板蓝灰、没有白翼斑，常守溪流；北红尾鸲雄鸟黑脸灰顶，翼上白斑醒目。"
    }
  },
  {
    id: "034",
    name: "白眉姬鹟",
    variant: "雄鸟",
    images: ["/birds/034.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "迁徙期和繁殖期可见于阔叶林，常从枝头飞出捕虫再返回。雄鸟黑白黄三色，对比强烈。",
    memory: "黑背配白长眉，白翼斑下面接黄腰。",
    keyMarks: ["长而清楚的白色眉纹", "黑色上体与白翼斑", "鲜黄色腰和下体"],
    similar: {
      name: "黄眉姬鹟",
      difference:
        "黄眉姬鹟雄鸟的眉纹和喉胸多呈橙黄；白眉姬鹟的眉纹纯白，黑白对比更强，腰部鲜黄。"
    }
  },
  {
    id: "035",
    name: "苍头燕雀",
    images: ["/birds/035.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "在中国主要见于西北及部分北方地区的林缘、农田和公园，常在地面寻找种子。雄鸟蓝灰色头顶、粉红胸和白翼斑组合鲜明。",
    memory: "蓝灰头、粉红胸，两道白翼斑，腰部泛绿。",
    keyMarks: ["蓝灰色头顶与后颈", "粉红至砖红色胸部", "两道白翼斑与绿色腰"],
    similar: {
      name: "燕雀",
      difference:
        "燕雀肩胸更橙，腰部纯白，冬羽头部常显斑驳；苍头燕雀雄鸟有整洁的蓝灰头、粉红胸和绿色腰。"
    }
  },
  {
    id: "036",
    name: "红喉姬鹟",
    images: ["/birds/036.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 3,
    intro:
      "迁徙季在林下较安静地捕捉飞虫，体形小、嘴细。雄鸟繁殖羽喉部有一块橙红色，其余体色偏冷灰。",
    memory: "灰色小姬鹟，喉咙点一枚橙红印章。",
    keyMarks: ["细小黑嘴", "橙红色局限在喉部", "灰褐上体与浅色眼圈"],
    similar: {
      name: "北红尾鸲",
      difference:
        "北红尾鸲体形更大，有橙红尾和醒目白翼斑；红喉姬鹟嘴更细，只在喉部有小块橙红色。"
    }
  },
  {
    id: "037",
    name: "三道眉草鹀",
    images: ["/birds/037.webp", "/birds/037-2.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "城市田野",
    difficulty: 2,
    intro:
      "常停在低山灌丛、农田边缘和裸露枝头鸣唱。雄鸟脸上黑白栗色条纹分明，像画了三道眉。",
    memory: "栗色头配黑白脸，三道横纹一眼认。",
    keyMarks: ["栗色头顶与耳羽", "黑白相间的三道脸纹", "褐色纵纹背部"],
    similar: {
      name: "黄喉鹀",
      difference:
        "黄喉鹀雄鸟喉部和眉纹鲜黄；三道眉草鹀没有黄色，脸部以栗、黑、白三色条纹为主。"
    }
  },
  {
    id: "038",
    name: "白腰文鸟",
    images: ["/birds/038.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 2,
    intro:
      "常成群活动于南方草地、农田和灌丛，用粗短嘴取食草籽。飞起时白色腰部会突然闪现。",
    memory: "褐头小文鸟，白腰像背后贴了一张标签。",
    keyMarks: ["醒目的白色腰部", "深褐色头与胸", "粗短铅灰色锥形嘴"],
    similar: {
      name: "斑文鸟",
      difference:
        "斑文鸟胸腹布满清楚的黑色鳞纹；白腰文鸟下体较素，飞行或转身时白腰最突出。"
    }
  },
  {
    id: "039",
    name: "白鹡鸰",
    images: ["/birds/039.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常在水边、道路、屋顶和草坪快步行走，停下时不断上下摆尾。黑白灰配色会因亚种和季节略有变化。",
    memory: "黑白灰长尾鸟，一边走一边点尾。",
    keyMarks: ["黑白相间的头胸", "灰至黑色背部", "不断摆动的黑白长尾"],
    similar: {
      name: "灰鹡鸰",
      difference:
        "灰鹡鸰腹部和尾下鲜黄，尾更长且偏爱溪流；白鹡鸰以黑白灰为主，下体没有大片黄色。"
    }
  },
  {
    id: "040",
    name: "苇鹀",
    images: ["/birds/040.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "芦苇湿地",
    difficulty: 3,
    intro:
      "多在芦苇荡和湿地草丛活动，冬羽及雌鸟呈褐色纵纹，繁殖期雄鸟换成黑头白领。粗短嘴显示它以种子为主食。",
    memory: "芦苇上的褐色纵纹鹀，嘴短，外侧尾羽白。",
    keyMarks: ["全身较重的褐色纵纹", "短小锥形嘴", "白色外侧尾羽"],
    similar: {
      name: "黄喉鹀雌鸟",
      difference:
        "黄喉鹀通常仍可见淡黄喉部和较清楚的脸纹；苇鹀纵纹更重、没有黄色，且更依赖芦苇湿地。"
    }
  },
  {
    id: "041",
    name: "东方大苇莺",
    images: ["/birds/041.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "芦苇湿地",
    difficulty: 2,
    intro:
      "夏季在芦苇顶部放声鸣唱，体形明显大于多数柳莺。身体整体棕褐，识别重点在厚重体形、强嘴和生境。",
    memory: "芦苇顶上的大号褐色莺，粗嘴唱得响。",
    keyMarks: ["大而厚重的体形", "粗长而有力的嘴", "淡眉纹与棕褐上体"],
    similar: {
      name: "褐柳莺",
      difference:
        "褐柳莺小巧、嘴细，常钻低矮灌丛；东方大苇莺大得多，嘴粗，常攀在芦苇中上部鸣唱。"
    }
  },
  {
    id: "042",
    name: "冠纹柳莺",
    images: ["/birds/042.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 3,
    intro:
      "繁殖于山地阔叶林，迁徙季也可在城市林地遇见。上体偏绿、下体洁白，头顶中央有一条浅色冠纹。",
    memory: "绿背白肚，长白眉，头顶再藏一条浅冠纹。",
    keyMarks: ["浅色中央冠纹", "长而清楚的眉纹", "绿色上体与洁白下体"],
    similar: {
      name: "巨嘴柳莺",
      difference:
        "巨嘴柳莺更褐、更厚重，下体偏黄褐且嘴粗；冠纹柳莺更绿白，头顶有浅冠纹并带翼斑。"
    }
  },
  {
    id: "043",
    name: "褐柳莺",
    images: ["/birds/043.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 3,
    intro:
      "常在低矮灌丛、沟边和湿地边缘迅速穿行，迁徙和越冬期较常见。整体朴素褐色，但眉纹清楚。",
    memory: "小、褐、细嘴，贴着灌丛不停甩尾。",
    keyMarks: ["灰褐色上体", "清楚浅眉与深眼纹", "细嘴并常摆尾"],
    similar: {
      name: "巨嘴柳莺",
      difference:
        "巨嘴柳莺体形更大、嘴和腿粗，下体更黄；褐柳莺小而纤细，整体偏冷褐，常在更低处活动。"
    }
  },
  {
    id: "044",
    name: "黑颈䴙䴘",
    images: ["/birds/044.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 2,
    intro:
      "擅长潜水的小型水鸟，冬季可成群出现在湖泊和沿海。繁殖羽黑颈配金色耳羽，红眼非常醒目。",
    memory: "黑头黑颈红眼睛，耳后炸开金丝羽。",
    keyMarks: ["鲜红色眼睛", "金黄色扇状耳羽", "细黑且微上翘的嘴"],
    similar: {
      name: "小䴙䴘",
      difference:
        "小䴙䴘体形更小、嘴短而常有浅色嘴端，没有红眼和金色耳羽；黑颈䴙䴘的嘴更细长。"
    }
  },
  {
    id: "045",
    name: "黑尾蜡嘴雀",
    variant: "雄鸟",
    images: ["/birds/045.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "常成群活动于林缘、公园和农田，粗厚的嘴能咬开坚硬种子。雄鸟黑色头部与浅灰褐身体对比强烈。",
    memory: "黑头配一枚粗大的蜡黄色嘴。",
    keyMarks: ["黑色头部", "粗厚蜡黄色嘴", "黑翼上的大白斑"],
    similar: {
      name: "锡嘴雀",
      difference:
        "锡嘴雀头部栗褐、黑喉明显，头和嘴显得更宽厚；雄性黑尾蜡嘴雀是整块黑头配浅色身体。"
    }
  },
  {
    id: "046",
    name: "黑尾蜡嘴雀",
    variant: "雌鸟",
    images: ["/birds/046.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 3,
    intro:
      "雌鸟没有雄鸟的黑头，整体灰褐，但仍保留极粗的蜡黄色嘴、黑翼白斑和较粗壮的体形。",
    memory: "灰褐头也不怕，先认粗蜡嘴和黑白翅。",
    keyMarks: ["粗厚蜡黄色嘴", "灰褐色头部", "黑色翼尾与白翼斑"],
    similar: {
      name: "锡嘴雀",
      difference:
        "锡嘴雀头更大、颈更短，体色偏暖栗褐并常见黑喉；雌性黑尾蜡嘴雀较灰，翼上白斑更整洁。"
    }
  },
  {
    id: "047",
    name: "黑尾鸥",
    images: ["/birds/047.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 2,
    intro:
      "东亚沿海常见的中大型鸥类，也会进入港口和河口。成鸟尾端保持宽黑带，黄色嘴端同时带红、黑斑。",
    memory: "黄嘴红黑尖，尾巴末端一条黑。",
    keyMarks: ["成鸟宽黑色尾带", "黄嘴带红黑端斑", "灰背与黄色腿"],
    similar: {
      name: "红嘴鸥",
      difference:
        "红嘴鸥更小，嘴腿均红，成鸟没有黑尾带；黑尾鸥更粗壮，黄嘴和黑尾带非常明确。"
    }
  },
  {
    id: "048",
    name: "红脚苦恶鸟",
    images: ["/birds/048.webp"],
    group: "鹭类与秧鸡",
    habitat: "芦苇湿地",
    difficulty: 2,
    intro:
      "栖息于河岸、稻田和湿地边缘的浓密植被，常贴地快走。身体栗褐，头胸偏灰，腿带暗红色。",
    memory: "灰脸栗褐身，红脚翘短尾，贴着湿地边走。",
    keyMarks: ["灰色脸与胸", "栗褐色身体", "暗红色腿与常翘短尾"],
    similar: {
      name: "白胸苦恶鸟",
      difference:
        "白胸苦恶鸟体形更大，脸、喉和胸大片纯白，背部近黑；红脚苦恶鸟更小，整体为灰栗色。"
    }
  },
  {
    id: "049",
    name: "红胁蓝尾鸲",
    variant: "雄鸟",
    images: ["/birds/049.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "冬季常在林下、灌丛和城市公园低处活动，喜欢在地面附近捕虫。雄鸟蓝色上体、白腹与橙色胁斑组合独特。",
    memory: "蓝背蓝尾，白肚两侧各点一团橙。",
    keyMarks: ["蓝色上体与尾", "白色下体", "两胁鲜橙色斑"],
    similar: {
      name: "红尾水鸲雄鸟",
      difference:
        "红尾水鸲雄鸟下体也呈蓝灰色、尾部栗红，常在溪流边；红胁蓝尾鸲腹部白，橙色只在两胁。"
    }
  },
  {
    id: "050",
    name: "黄喉鹀",
    variant: "雄鸟",
    images: ["/birds/050.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "常在林缘、灌丛和农田边活动。雄鸟的亮黄喉部与黑色脸纹反差鲜明，头顶和背部呈栗褐色。",
    memory: "栗色帽、黑面罩，喉咙亮出一块黄。",
    keyMarks: ["鲜黄色喉部和眉纹", "黑色脸纹与胸带", "栗褐色头顶"],
    similar: {
      name: "三道眉草鹀",
      difference:
        "三道眉草鹀脸部是栗、黑、白三色条纹，完全没有亮黄喉；黄喉鹀的黄色区域十分醒目。"
    }
  },
  {
    id: "051",
    name: "灰鹡鸰",
    images: ["/birds/051.webp"],
    group: "城市与田野鸟",
    habitat: "山地溪流",
    difficulty: 1,
    intro:
      "偏爱清澈溪流、河道和水坝，常在石头上不断摆动超长尾。虽然叫灰鹡鸰，腹部和尾下却带鲜黄色。",
    memory: "灰背黄腹，尾巴比身体还显得长。",
    keyMarks: ["灰色背部", "黄色腹部与尾下", "极长的黑白尾"],
    similar: {
      name: "白鹡鸰",
      difference:
        "白鹡鸰全身以黑白灰为主、没有大片黄色；灰鹡鸰腹部和尾下鲜黄，尾也明显更长。"
    }
  },
  {
    id: "052",
    name: "灰椋鸟",
    images: ["/birds/052.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常成群出现在城市草坪、农田和村落，秋冬可形成大群。白色脸颊、橙色嘴和飞行时的白腰是识别重点。",
    memory: "灰褐椋鸟白脸颊，橙嘴，转身露白腰。",
    keyMarks: ["明显白色脸颊", "橙色嘴", "白色腰部与翼斑"],
    similar: {
      name: "乌鸫",
      difference:
        "乌鸫雄鸟通体黑、尾较长，只有嘴和眼圈橙黄；灰椋鸟更灰褐，白脸、白腰和白翼斑都明显。"
    }
  },
  {
    id: "053",
    name: "灰头绿啄木鸟",
    images: ["/birds/053.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 1,
    intro:
      "常见于开阔林地、果园和城市公园，也会落地寻找蚂蚁。灰色头部与绿色背翼构成最直接的识别组合。",
    memory: "灰头绿外套，眼边一撇黑胡子。",
    keyMarks: ["灰色头颈", "绿色背部与双翼", "黑色髭纹"],
    similar: {
      name: "大斑啄木鸟",
      difference:
        "大斑啄木鸟背翼黑白相间、尾下鲜红；灰头绿啄木鸟上体大面积绿色，头部素灰。"
    }
  },
  {
    id: "054",
    name: "金翅雀",
    images: ["/birds/054.webp", "/birds/054-2.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "常成群活动于林缘、农田和城市绿地，以种子为食。无论停栖还是飞行，翼和尾上的亮黄色块都很抢眼。",
    memory: "橄榄小雀灰粗嘴，翅尾各亮一块金黄。",
    keyMarks: ["翼上宽黄色斑", "尾基黄色斑", "粗短浅色锥形嘴"],
    similar: {
      name: "黄雀",
      difference:
        "黄雀体形更小、嘴更细，身体纵纹更重，雄鸟常有黑顶；金翅雀更粗壮，嘴厚且黄翼斑更宽。"
    }
  },
  {
    id: "055",
    name: "普通鵟",
    images: ["/birds/055.webp"],
    group: "天空飞鸟与猛禽",
    habitat: "开阔天空",
    difficulty: 2,
    intro:
      "冬季常见的中型猛禽，喜欢在农田、山坡上空盘旋，也会停在电杆。羽色变化很大，轮廓比颜色更可靠。",
    memory: "宽圆翅、短扇尾，盘旋时像一块结实的板。",
    keyMarks: ["宽而圆的双翼", "较短的扇形尾", "翼腕常有深色斑"],
    similar: {
      name: "黑鸢",
      difference:
        "黑鸢翅更长窄，尾部有明显浅叉；普通鵟翼宽圆、尾短而展开成完整扇形。"
    }
  },
  {
    id: "056",
    name: "普通楼燕",
    images: ["/birds/056.webp"],
    group: "天空飞鸟与猛禽",
    habitat: "开阔天空",
    difficulty: 2,
    intro:
      "几乎一生都在空中高速飞行，常在城市建筑和山崖洞隙繁殖。镰刀形长翼与短叉尾构成独特剪影。",
    memory: "通体暗色，双翼像两把弯镰刀。",
    keyMarks: ["极长的镰刀形翼", "短而浅叉的尾", "除浅喉外整体暗褐"],
    similar: {
      name: "白腰雨燕",
      difference:
        "白腰雨燕腰部有醒目白带，尾叉通常更深；普通楼燕从背面看近乎通体暗色，没有白腰。"
    }
  },
  {
    id: "057",
    name: "普通秋沙鸭",
    variant: "雄鸟",
    images: ["/birds/057.webp"],
    group: "雁鸭与游禽",
    habitat: "湖泊河流",
    difficulty: 1,
    intro:
      "冬季常见于宽阔河流、湖泊和水库，是擅长潜水捕鱼的鸭类。细长红嘴边缘有锯齿，身体显得低而修长。",
    memory: "绿头白身黑背，红色细长钩嘴。",
    keyMarks: ["深绿色头部", "洁白身体与黑背", "细长红色钩端嘴"],
    similar: {
      name: "红胸秋沙鸭",
      difference:
        "红胸秋沙鸭雄鸟有蓬松冠羽、白颈环和斑驳胸部；普通秋沙鸭头部更平滑，体侧大面积洁白。"
    }
  },
  {
    id: "058",
    name: "普通秧鸡",
    images: ["/birds/058.webp"],
    group: "鹭类与秧鸡",
    habitat: "芦苇湿地",
    difficulty: 2,
    intro:
      "藏身芦苇和湿地草丛，身体侧扁，能够在密集植被间穿行。长红嘴、蓝灰脸胸和黑白横斑体侧最关键。",
    memory: "长红嘴、灰胸、斑马纹体侧，悄悄钻芦苇。",
    keyMarks: ["细长红色嘴", "蓝灰色脸胸", "黑白横斑体侧"],
    similar: {
      name: "红脚苦恶鸟",
      difference:
        "红脚苦恶鸟嘴较短，身体以灰栗色为主、体侧没有强烈横斑；普通秧鸡拥有细长红嘴和斑纹体侧。"
    }
  },
  {
    id: "059",
    name: "青脚鹬",
    images: ["/birds/059.webp"],
    group: "滨海涉禽与鸥",
    habitat: "海岸滩涂",
    difficulty: 2,
    intro:
      "迁徙和越冬期常见于滩涂、盐田和浅水湿地，边走边用嘴扫探。腿呈灰绿色，嘴粗而微微上翘。",
    memory: "青绿色长腿，粗嘴尖端略向上挑。",
    keyMarks: ["灰绿色长腿", "粗长且微上翘的嘴", "灰白相间的中大型体形"],
    similar: {
      name: "泽鹬",
      difference:
        "泽鹬更纤细，腿更长，嘴像针一样细直；青脚鹬体形厚重，嘴基粗并有明显上翘感。"
    }
  },
  {
    id: "060",
    name: "山噪鹛",
    images: ["/birds/060.webp"],
    group: "林鸟与灌丛鸟",
    habitat: "林地灌丛",
    difficulty: 2,
    intro:
      "中国北方和中部山地常见的留鸟，常成小群在林下和灌丛中发出响亮叫声。体形较大，羽色几乎均一灰褐。",
    memory: "大号灰褐长尾鸟，粗弯嘴，成群在山林里吵。",
    keyMarks: ["均一灰褐色体羽", "较长尾羽", "粗而略下弯的嘴"],
    similar: {
      name: "山鹛",
      difference:
        "山鹛体形更小，有宽白眉、黑眼纹和胸部纵纹；山噪鹛更大、更均一，几乎没有醒目头纹。"
    }
  },
  {
    id: "061",
    name: "树麻雀",
    images: ["/birds/061.webp"],
    group: "城市与田野鸟",
    habitat: "城市田野",
    difficulty: 1,
    intro:
      "中国城乡最常见的鸟之一，常在人类居住区成群取食。雌雄外观相似，都有栗色头顶和黑色脸颊斑。",
    memory: "栗色整顶帽，白脸上按一枚黑手印。",
    keyMarks: ["栗色头顶与后颈", "白颊上的黑色圆斑", "黑色喉斑"],
    similar: {
      name: "家麻雀",
      difference:
        "雄性家麻雀头顶灰色、脸颊没有黑斑，雌鸟更是素褐；树麻雀雌雄都有栗色头顶和黑颊斑。"
    }
  }
];

export const birdLabel = (bird: Bird) =>
  bird.variant ? `${bird.name}（${bird.variant}）` : bird.name;
